/**
 * User Adapter for Unified Authentication System
 * 
 * Converts between the unified User model and the admin panel's expected AdminUser interface.
 * This adapter ensures compatibility between the two systems during the migration.
 */

import { AdminUser, AdminRole } from '../types'

// Unified system user interface (from backend)
export interface UnifiedUser {
  id: string
  email: string
  user_type: string
  admin_role?: string
  first_name?: string
  last_name?: string
  company?: string
  is_active: boolean
  is_verified: boolean
  created_at: string
  updated_at?: string
}

/**
 * Maps user_type and admin_role from unified system to AdminRole enum
 */
export const mapUserTypeToAdminRole = (adminRole?: string, userType?: string): AdminRole => {
  // If user_type is "admin" and no specific admin_role is set, default to SUPER_ADMIN
  if (userType === 'admin' && (!adminRole || adminRole === null || adminRole === undefined)) {
    return AdminRole.SUPER_ADMIN
  }
  
  switch (adminRole) {
    case 'super_admin':
      return AdminRole.SUPER_ADMIN
    case 'operations_manager':
      return AdminRole.OPERATIONS_MANAGER
    case 'support_agent':
      return AdminRole.SUPPORT_AGENT
    case 'read_only':
      return AdminRole.READ_ONLY
    default:
      // For admin users without specific role, default to SUPER_ADMIN
      if (userType === 'admin') {
        return AdminRole.SUPER_ADMIN
      }
      // For non-admin users, default to READ_ONLY for safety
      return AdminRole.READ_ONLY
  }
}

/**
 * Maps AdminRole enum back to string for API calls
 */
export const mapAdminRoleToString = (role: AdminRole): string => {
  switch (role) {
    case AdminRole.SUPER_ADMIN:
      return 'super_admin'
    case AdminRole.OPERATIONS_MANAGER:
      return 'operations_manager'
    case AdminRole.SUPPORT_AGENT:
      return 'support_agent'
    case AdminRole.READ_ONLY:
      return 'read_only'
    default:
      return 'read_only'
  }
}

/**
 * Converts a unified User to AdminUser interface expected by the admin panel
 */
export const adaptUserToAdminUser = (user: UnifiedUser): AdminUser => {
  // Generate username from email (common pattern)
  const username = user.email.split('@')[0]
  
  return {
    id: user.id,
    username: username,
    email: user.email,
    role: mapUserTypeToAdminRole(user.admin_role, user.user_type),
    mfa_enabled: false, // Unified system uses magic links, not MFA
    last_login: user.updated_at,
    created_at: user.created_at
  }
}

/**
 * Validates that a user has admin privileges
 */
export const validateAdminUser = (user: UnifiedUser): boolean => {
  return user.user_type === 'admin' && user.is_active && user.is_verified
}

/**
 * Gets display name for a user
 */
export const getUserDisplayName = (user: UnifiedUser): string => {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`
  }
  if (user.first_name) {
    return user.first_name
  }
  return user.email.split('@')[0]
}

/**
 * Permission mapping from AdminRole to permission strings
 */
export const ROLE_PERMISSIONS = {
  [AdminRole.SUPER_ADMIN]: ['*'],
  [AdminRole.OPERATIONS_MANAGER]: [
    'users:read', 'users:write', 'users:delete',
    'mls:read', 'mls:write', 'generations:read',
    'payments:read', 'analytics:read', 'system:read',
    'workflows:read', 'workflows:write', 'audit:read',
    'settings:read'
  ],
  [AdminRole.SUPPORT_AGENT]: [
    'users:read', 'users:write', 'generations:read',
    'support:read', 'support:write', 'mls:read',
    'workflows:read'
  ],
  [AdminRole.READ_ONLY]: [
    'users:read', 'generations:read', 'analytics:read',
    'mls:read', 'system:read',
    'workflows:read'
  ]
}

/**
 * Checks if a user has a specific permission
 */
export const checkUserPermission = (user: UnifiedUser, permission: string): boolean => {
  if (!validateAdminUser(user)) {
    return false
  }
  
  const role = mapUserTypeToAdminRole(user.admin_role, user.user_type)
  const permissions = ROLE_PERMISSIONS[role] || []
  
  // Super admin has all permissions
  if (permissions.includes('*')) {
    return true
  }
  
  return permissions.includes(permission)
}

/**
 * Checks if a user has a specific role
 */
export const checkUserRole = (user: UnifiedUser, requiredRole: AdminRole): boolean => {
  if (!validateAdminUser(user)) {
    return false
  }
  
  const userRole = mapUserTypeToAdminRole(user.admin_role, user.user_type)
  return userRole === requiredRole
}